import { Button, Spinner, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { DeleteAgamiDialog } from '../components/DeleteAgamiDialog'
import NoUploads from '../components/NoUploads'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useSessionContext } from '../contexts/SessionContext'
import { useUserAgamiLibrary } from '../hooks/useUserAgamiLibrary'
import { AgamiLibraryItem } from '../types/agami-library'
import {
  AgamiLibraryQuery,
  parseAgamiLibraryQuery,
  updateAgamiLibraryQuery,
} from '../utils/helpers/agami-library.helpers'
import { AgamiLibraryControls } from './AgamiLibraryControls'
import { AgamiLibraryPagination } from './AgamiLibraryPagination'
import { AgamiLibrarySummary } from './AgamiLibrarySummary'
import { AgamiLibraryTable } from './AgamiLibraryTable'

export const AgamiLibrary = () => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const { darkMode } = useDarkMode()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedForDeletion, setSelectedForDeletion] = useState<AgamiLibraryItem | null>(null)
  const query = useMemo(() => parseAgamiLibraryQuery(searchParams), [searchParams])
  const { data, resolvedRequestPage, isLoading, error, refetch } = useUserAgamiLibrary(query)

  const updateQuery = useCallback(
    (changes: Partial<AgamiLibraryQuery>) => {
      setSearchParams(updateAgamiLibraryQuery(searchParams, changes))
    },
    [searchParams, setSearchParams]
  )

  useEffect(() => {
    if (data && resolvedRequestPage === query.page && data.page !== query.page) {
      setSearchParams(updateAgamiLibraryQuery(searchParams, { page: data.page }), { replace: true })
    }
  }, [data, query.page, resolvedRequestPage, searchParams, setSearchParams])

  const resetFilters = () =>
    updateQuery({ status: 'all', search: '', activationType: null, mediaType: null, language: null })

  const handleDeleted = () => {
    if (data?.items.length === 1 && query.page > 1) updateQuery({ page: query.page - 1 })
    else refetch()
  }

  if (!data && isLoading) {
    return <Spinner className="h-10 w-10 m-auto mt-8" />
  }

  if (!data && error) {
    return (
      <div className="text-center py-10">
        <Typography className="dark:text-gray-200 mb-3">{t('agamiLibrary.error')}</Typography>
        <Button
          size="sm"
          variant="outlined"
          color={darkMode ? 'white' : ('primary' as colors)}
          onClick={refetch}
        >
          {t('agamiLibrary.retry')}
        </Button>
      </div>
    )
  }

  if (!data) return null

  const hasFilters = Boolean(
    query.status !== 'all' || query.search || query.activationType || query.mediaType || query.language
  )
  const inventoryEmpty = !hasFilters && data.facets.status.all === 0

  return (
    <div className="pb-8" aria-busy={isLoading} data-testid="agami-library-v2">
      {error && (
        <div
          role="alert"
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30 p-3 mb-4"
        >
          <Typography className="text-red-900 dark:text-red-100">{t('agamiLibrary.error')}</Typography>
          <Button size="sm" variant="outlined" color="red" onClick={refetch}>
            {t('agamiLibrary.retry')}
          </Button>
        </div>
      )}
      <AgamiLibrarySummary facets={data.facets} />
      <AgamiLibraryControls
        query={query}
        statusFacets={data.facets.status}
        activationFacets={data.facets.activation}
        mediaFacets={data.facets.media}
        languageFacets={data.facets.languages}
        isLoading={isLoading}
        onChange={updateQuery}
        onReset={resetFilters}
      />

      {data.items.length === 0 ? (
        <NoUploads>
          <div className="text-center dark:text-gray-200">
            <Typography>
              {inventoryEmpty ? t('agamiLibrary.empty.inventory') : t('agamiLibrary.empty.results')}
            </Typography>
            {inventoryEmpty ? (
              <Link to="/upload/v2" className="underline font-bold">
                {t('agamiLibrary.empty.create')}
              </Link>
            ) : (
              <Button size="sm" variant="text" className="underline dark:text-accent" onClick={resetFilters}>
                {t('agamiLibrary.filters.reset')}
              </Button>
            )}
          </div>
        </NoUploads>
      ) : (
        <AgamiLibraryTable items={data.items} onDelete={setSelectedForDeletion} />
      )}

      <AgamiLibraryPagination
        page={data.page}
        pageSize={data.pageSize}
        total={data.filteredTotal}
        onPageChange={(page) => updateQuery({ page })}
      />

      {selectedForDeletion && (
        <DeleteAgamiDialog
          open
          userId={user?.id ?? ''}
          agami={{
            id: selectedForDeletion.id,
            name: selectedForDeletion.name,
            mediaPath: selectedForDeletion.mediaPath,
            mediaType: selectedForDeletion.mediaType,
          }}
          onClose={() => setSelectedForDeletion(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}
